import { ShipmentInterface } from 'features/ShipmentDetails'
import { action, observable } from 'mobx'
import { API } from 'service/API'
import { AuthStore } from 'service/Auth'
import { getAPIBaseUrl, getUrlConstructorByPrefix, URLPrefix } from 'service/url'

import { DocumentInterface } from '../Documents'
import { DocumentV2, DocumentV2Source, DocumentV2State, mapToDocumentInterface } from '../DocumentsV2'
import { ProgressEventHandler } from './DocumentsUpload'

export interface DocumentsStatus {
  deleting: boolean
  uploading: boolean
}

type DocumentPayload = {
  state: DocumentV2State
  mimeType: Blob['type']
  customerID: string
  source: DocumentV2Source
  documentTypeCode: string
}

type UpdateFileParams = {
  file: File
  entityType: string
  entityID: string
  documentID?: string
  payload: DocumentPayload
  shipment: ShipmentInterface
  onProgress?: ProgressEventHandler
}

type MethodType = 'POST' | 'PUT'

function uploadFile(
  file: File,
  url: string,
  params: { [key: string]: string },
  token?: string,
  handlers?: EventHandlers,
  method: MethodType = 'POST'
): XMLHttpRequest {
  const formData = new FormData()
  formData.append('file', file)

  Object.keys(params).forEach((key) => {
    formData.append(key, params[key])
  })

  const req = new XMLHttpRequest()
  req.open(method, url)
  if (token) {
    req.setRequestHeader('Authorization', `Bearer ${token}`)
  }
  // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
  req.onload = handlers.onload
  // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
  req.onerror = handlers.onerror
  req.send(formData)
  return req
}

const downloadBlob = (blob: Blob, name: string) => {
  // @ts-expect-error Property 'msSaveOrOpenBlob' does not exist on type 'Navigator'.
  if (typeof navigator !== 'undefined' && navigator.msSaveOrOpenBlob) {
    // @ts-expect-error Property 'msSaveOrOpenBlob' does not exist on type 'Navigator'.
    navigator.msSaveOrOpenBlob(blob, name)
    return
  }
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

export class UploadStore {
  authStore: AuthStore
  documentsStatus: DocumentsStatus

  @observable
  uploading = false

  @observable
  progress = 0

  token: string
  API: API

  reportsDocument = {
    download: async (documentID: string, name: string) => {
      const response = await fetch(
        getUrlConstructorByPrefix(URLPrefix.Documents)(`/documents/${documentID}/download`),
        {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        }
      )
      const blob = await response.blob()
      downloadBlob(blob, name)
    },
  }

  shipmentFile = {
    getUrl: (shipmentId: string, documentId?: string, authorized?: boolean) => {
      let url = `/shipments/${shipmentId}/documents`
      if (documentId) {
        url += `/${documentId}`
      }
      if (authorized) {
        url += `?jwt=${this.token}`
      }
      return getAPIBaseUrl(url)
    },
  }

  bookingFile = {
    getUrl: (bookingID: string, documentID?: string) => {
      const url = `/documents/booking/${bookingID}`
      return getUrlConstructorByPrefix(URLPrefix.Documents)(documentID ? `${url}/${documentID}` : url)
    },

    upload: (
      file: File,
      bookingID: string,
      json: string,
      onProgress?: ProgressEventHandler
    ): Promise<DocumentInterface> => {
      return this.upload(
        file,
        this.bookingFile.getUrl(bookingID),
        {
          json,
        },
        onProgress
      )
    },

    replace: (
      file: File,
      bookingID: string,
      json: string,
      documentID: string,
      onProgress?: ProgressEventHandler
    ): Promise<DocumentInterface> => {
      return this.upload(
        file,
        this.bookingFile.getUrl(bookingID, documentID),
        {
          json,
        },
        onProgress,
        'PUT'
      )
    },
  }

  // ToDo: Refactor to make booking also use this and remove bookingfile
  documentServiceV2 = {
    createURL: (entityType: string, entityId?: string, documentId?: string) => {
      let url = `/documents/${entityType}`

      if (entityId) {
        url += `/${entityId}`
        if (documentId) {
          url += `/${documentId}`
        }
      }

      return getUrlConstructorByPrefix(URLPrefix.Documents)(`${url}`)
    },

    uploadFile: (
      file: File,
      entityType: string,
      entityID: string,
      json: string,
      onProgress?: ProgressEventHandler
    ): Promise<DocumentV2> => {
      return this.upload(
        file,
        this.documentServiceV2.createURL(entityType, entityID),
        {
          json,
        },
        onProgress
      )
    },

    updateFile: ({
      file,
      documentID,
      entityType,
      entityID,
      payload,
      shipment,
      onProgress,
    }: UpdateFileParams): Promise<DocumentInterface> => {
      return this.upload(
        file,
        this.documentServiceV2.createURL(entityType, entityID, documentID),
        {
          json: JSON.stringify(payload),
        },
        onProgress,
        'PUT'
      ).then((res) => {
        if (res?.message) {
          throw new Error(res.message || 'Failed to replace document')
        }
        if (documentID) {
          const oldIndex = shipment.documents.findIndex((doc) => doc._id === documentID)
          shipment.documents.splice(oldIndex, 1, mapToDocumentInterface(res))
        }
        return res
      })
    },

    deleteFile: async (entityType: string, entityID: string, documentID: string) => {
      return fetch(
        getUrlConstructorByPrefix(URLPrefix.Documents)(`/documents/${entityType}/${entityID}/${documentID}`),
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        }
      )
    },

    downloadAllFiles: async (entityType: string, entityID: string) => {
      const response = await fetch(
        getUrlConstructorByPrefix(URLPrefix.Documents)(
          `/documents/customers/${this.authStore.state.customer._id}/${entityType}/${entityID}/download`
        ),
        {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        }
      )
      const blob = await response.blob()
      downloadBlob(blob, entityID)
    },

    downloadFile: async (entityType: string, entityID: string, documentID: string, documentName: string) => {
      const response = await fetch(
        getUrlConstructorByPrefix(URLPrefix.Documents)(`/documents/${entityType}/${entityID}/${documentID}/download`),
        {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        }
      )

      const blob = await response.blob()
      downloadBlob(blob, documentName)
    },
  }

  customerFile = {
    getUrl: (docId?: string, authorized?: boolean) => {
      let url = `/customers/${this.authStore.state.customer._id}/documents/`
      if (docId) {
        url += docId
      }
      if (authorized) {
        url += `?jwt=${this.token}`
      }
      return getAPIBaseUrl(url)
    },
    upload: (file: File, role: string) => {
      return this.upload(file, this.customerFile.getUrl(), {
        role,
      }).then((newDoc: DocumentInterface) => {
        this.authStore.reloadCustomer()
        return newDoc
      })
    },
    replace: (file: File, doc: DocumentInterface) => {
      return this.upload(file, this.customerFile.getUrl(doc._id), {
        role: doc.role,
      }).then(() => {
        this.authStore.reloadCustomer()
      })
    },
    delete: (doc: DocumentInterface) => {
      const { customer } = this.authStore.state
      this.documentsStatus.deleting = true
      return this.API.customers.documents.delete(customer._id, doc._id).then(() => {
        this.authStore.reloadCustomer()
        this.documentsStatus.deleting = false
      })
    },
  }

  constructor(authStore: AuthStore, API: API, documentsCommonStatus?: DocumentsStatus) {
    this.authStore = authStore
    this.API = API
    this.token = authStore.state.token.replace('JWT ', '')
    this.documentsStatus = documentsCommonStatus || {
      uploading: false,
      deleting: false,
    }
  }

  @action
  updateProgress({ loaded, total }: ProgressEvent) {
    this.progress = loaded / total
  }

  @action
  upload = (
    file: File,
    url: string,
    params: { [key: string]: string },
    _onProgress?: ProgressEventHandler,
    method?: MethodType
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  ): Promise<any> => {
    this.uploading = true
    this.documentsStatus.uploading = true
    const promise = new Promise((resolve, reject) => {
      uploadFile(
        file,
        url,
        params,
        this.authStore.state.token,
        {
          onload: (event) => {
            this.progress = 0
            this.uploading = false
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
            resolve(JSON.parse((event.target as any).response))
            this.documentsStatus.uploading = false
          },
          onerror: (err) => {
            this.uploading = false
            this.documentsStatus.uploading = false
            reject(err)
          },
        },
        method
      )
    })
    return promise
  }
}

interface EventHandlers {
  onload?
  onerror?
  onabort?
}
